import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { X, Download, Mail, FolderUp, Pencil, Trash2, ExternalLink, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { PO_API, poAuth, poPdfUrl, money, fmtPoDate, INPUT, LABEL, BTN_PRIMARY, BTN_GHOST } from './poUtils';

export default function PODetail({ poNumber, canCreate, onClose, onEdit, onChanged }) {
  const navigate = useNavigate();
  const [po, setPo] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState({ to: '', cc: '', subject: '', body: '' });
  const [busy, setBusy] = useState('');

  const load = () => axios.get(`${PO_API}/${poNumber}`, poAuth()).then(r => { setPo(r.data); setEmail(e => ({ ...e, to: e.to || r.data.vendor_email || '' })); }).catch(() => toast.error('Could not load the PO'));
  useEffect(() => { load(); }, [poNumber]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const fileToDrive = async () => {
    setBusy('drive');
    try {
      const r = await axios.post(`${PO_API}/${poNumber}/file-to-drive`, {}, poAuth());
      toast.success(r.data.folder_created ? 'Created the job folder and filed the PO' : 'PO filed in the job’s Purchase Orders folder');
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Drive filing failed'); } finally { setBusy(''); }
  };
  const sendEmail = async () => {
    if (!email.to.trim()) return toast.error('Add the vendor’s email');
    setBusy('email');
    try {
      await axios.post(`${PO_API}/${poNumber}/email`, email, poAuth());
      toast.success(`PO ${poNumber} emailed to ${email.to}`);
      setEmailOpen(false); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Email failed'); } finally { setBusy(''); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete PO ${poNumber}? The sheet row is cleared and the number goes back in the pool.`)) return;
    setBusy('delete');
    try {
      await axios.delete(`${PO_API}/${poNumber}`, poAuth());
      toast.success(`PO ${poNumber} deleted and the sheet row cleared`);
      onChanged?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); } finally { setBusy(''); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-slate-900/40 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="po-detail-drawer">
      <div className="h-full w-full max-w-3xl bg-white/95 backdrop-blur-xl shadow-2xl border-l border-white/80 flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200/80">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-black text-slate-900 tracking-tight">PO {poNumber}</h2>
              {po?.work_order_number && (
                <button onClick={() => po.project?.id ? navigate(`/projects/${po.project.id}`) : null} className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-sky-100 text-sky-900 hover:bg-sky-200" data-testid="po-detail-wo">{po.work_order_number}</button>
              )}
              {po?.created_in_ops && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">raised in Ops</span>}
            </div>
            {po && <p className="text-sm text-slate-600 truncate mt-0.5">{po.company_name}{po.vendor_contact ? ` · ${po.vendor_contact}` : ''} · {fmtPoDate(po.date)} · {po.ordered_by}</p>}
            {po?.project && <p className="text-xs text-slate-500 truncate">{po.project.client_name} — {po.project.title}</p>}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 shrink-0" data-testid="po-detail-close"><X className="w-5 h-5" /></button>
        </div>

        {po && (
          <div className="px-5 py-3 border-b border-slate-200/80 flex items-center gap-2 flex-wrap" data-testid="po-detail-actions">
            <a href={poPdfUrl(poNumber)} target="_blank" rel="noreferrer" className={BTN_GHOST} data-testid="po-download"><Download className="w-4 h-4" /> PDF</a>
            {canCreate && <button onClick={() => setEmailOpen(o => !o)} className={BTN_GHOST} data-testid="po-email-btn"><Mail className="w-4 h-4" /> Email vendor</button>}
            {canCreate && <button onClick={fileToDrive} disabled={busy === 'drive'} className={BTN_GHOST} data-testid="po-drive-btn">{busy === 'drive' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderUp className="w-4 h-4" />} File to job folder</button>}
            {po.can_edit && <button onClick={() => onEdit(po)} className={BTN_GHOST} data-testid="po-edit-btn"><Pencil className="w-4 h-4" /> Edit</button>}
            {po.can_edit && <button onClick={remove} disabled={busy === 'delete'} className={BTN_GHOST + ' hover:border-red-300 hover:text-red-700 ml-auto'} data-testid="po-delete-btn"><Trash2 className="w-4 h-4" /> Delete</button>}
          </div>
        )}

        {po && (po.drive_url || po.emailed_at) && (
          <div className="px-5 py-2 border-b border-slate-100 flex items-center gap-4 flex-wrap text-xs text-slate-600">
            {po.drive_url && <a href={po.drive_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sky-700 hover:text-sky-900 font-semibold" data-testid="po-drive-link"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Filed in Drive <ExternalLink className="w-3 h-3" /></a>}
            {po.emailed_at && <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Emailed to {po.emailed_to}</span>}
          </div>
        )}

        {emailOpen && (
          <div className="px-5 py-4 border-b border-slate-200/80 bg-slate-50/80 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="po-email-form">
            <div><label className={LABEL}>To</label><input value={email.to} onChange={e => setEmail({ ...email, to: e.target.value })} className={INPUT} placeholder="orders@vendor.com" data-testid="po-email-to" /></div>
            <div><label className={LABEL}>Cc</label><input value={email.cc} onChange={e => setEmail({ ...email, cc: e.target.value })} className={INPUT} data-testid="po-email-cc" /></div>
            <div className="sm:col-span-2"><label className={LABEL}>Subject</label><input value={email.subject} onChange={e => setEmail({ ...email, subject: e.target.value })} className={INPUT} placeholder={`365 Industrial Solutions — Purchase Order ${poNumber}`} /></div>
            <div className="sm:col-span-2"><label className={LABEL}>Message <span className="normal-case font-normal text-slate-400">(leave blank for the standard note)</span></label><textarea value={email.body} onChange={e => setEmail({ ...email, body: e.target.value })} rows={3} className={INPUT} /></div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button onClick={() => setEmailOpen(false)} className={BTN_GHOST}>Cancel</button>
              <button onClick={sendEmail} disabled={busy === 'email'} className={BTN_PRIMARY} data-testid="po-email-send">{busy === 'email' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send with PDF from my mailbox</button>
            </div>
          </div>
        )}

        <div className="flex-1 min-h-0 bg-slate-100/70">
          <iframe title={`PO ${poNumber}`} src={`${poPdfUrl(poNumber)}#toolbar=0&navpanes=0`} className="w-full h-full" data-testid="po-pdf-frame" />
        </div>
        {po && (
          <div className="px-5 py-2 border-t border-slate-200/80 text-xs text-slate-600 flex items-center gap-4 flex-wrap">
            <span>Subtotal <b className="text-slate-900">{money(po.amount)}</b></span>
            <span>HST <b className="text-slate-900">{money(po.tax)}</b></span>
            <span>Total <b className="text-sky-800">{money(po.total_amount)}</b></span>
            {po.description && <span className="truncate text-slate-500 ml-auto max-w-[50%]">{po.description}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
